import { h } from 'preact';
import { useState, useEffect, useRef } from 'preact/hooks';
import { route } from 'preact-router';
import { api } from '../state/auth.js';
import { loadTheme } from '../state/theme.js';
import { useTheme } from '../hooks/useTheme.js';
import { setup, loadSetup, changeSetup } from '../state/setup.js';
import { lockOf } from '../utils/plan.js';
import Button from '../components/shared/Button.jsx';

// Inrichten: de route waarlangs een nieuwe club zichzelf inricht (config/setup-route.js).
// Links de fases met hun stappen, rechts de gekozen stap: waarom, wat het oplost, wat ermee
// samenhangt, en in fase 1 een eigen invulscherm met voorbeelden voor de sport van de club.

const STATUS_ICON = { done: '✓', later: '⏸', open: '' };

export default function SetupView({ step: stepKey }) {
  const data = setup.value;
  useEffect(() => { loadSetup({ force: true }); }, []);

  if (!data) return <div class="text-center text-muted" style="padding:60px">Laden…</div>;
  if (data.unavailable) {
    return (
      <div class="card" style="padding:40px;text-align:center;max-width:520px;margin:40px auto">
        <div style="font-size:32px;margin-bottom:10px">🧭</div>
        <div style="font-weight:700;margin-bottom:6px">De inrichting is voor de beheerder van de club</div>
        <div class="text-sm text-muted">Vraag de beheerder van jullie club als je wilt meehelpen met inrichten.</div>
      </div>
    );
  }
  const steps = data.steps || [];
  // Zonder gekozen stap: de eerste die nog open staat
  const current = steps.find(s => s.key === stepKey) || steps.find(s => s.status === 'open') || steps[0];
  const index = steps.indexOf(current);
  const next = steps.slice(index + 1).find(s => s.status === 'open') || steps[index + 1];

  return (
    <div>
      <div class="page-header">
        <div>
          <h1 class="page-title">Inrichten</h1>
          <div class="text-sm text-muted">Stap voor stap de app klaarmaken voor jullie club. Je kunt zelf kiezen waar je verdergaat; fase 1 eerst.</div>
        </div>
        <VisibilityToggle hidden={data.hidden} />
      </div>
      <Progress data={data} />
      <div class="setup-layout" style="display:grid;grid-template-columns:minmax(220px,280px) minmax(0,1fr);gap:20px;align-items:start">
        <PhaseList data={data} current={current} />
        <StepPanel key={current.key} step={current} next={next} data={data} />
      </div>
    </div>
  );
}

// De route in menu, dashboard en welkomstvenster: uit, of weer aan (ook vanuit Instellingen)
export function VisibilityToggle({ hidden }) {
  const [error, setError] = useState('');
  const toggle = () => changeSetup('/api/setup/visibility', { method: 'PUT', body: JSON.stringify({ hidden: !hidden }) })
    .then(() => setError(''), () => setError('Dat lukte niet. Probeer het nog eens.'));
  return (
    <div style="text-align:right">
      <Button variant="ghost" size="sm" onClick={toggle}>{hidden ? 'Weer tonen in menu en dashboard' : 'Verbergen uit menu en dashboard'}</Button>
      {error && <div class="text-sm" style="color:var(--color-danger)">{error}</div>}
    </div>
  );
}

function Progress({ data }) {
  const pct = Math.round((data.done / data.total) * 100);
  return (
    <div class="card" style="padding:14px 18px;margin-bottom:20px;display:flex;align-items:center;gap:16px">
      <div style="flex:1">
        <div style="height:8px;border-radius:4px;background:var(--color-bg-secondary);overflow:hidden">
          <div style={`height:100%;width:${pct}%;background:var(--color-primary);transition:width .3s`} />
        </div>
      </div>
      <div class="text-sm" style="white-space:nowrap"><strong>{data.done} van {data.total}</strong> stappen klaar</div>
    </div>
  );
}

function PhaseList({ data, current }) {
  return (
    <div class="card" style="padding:8px 0">
      {data.phases.map((p, i) => (
        <div key={p.key} style="padding:8px 0">
          <div style="padding:4px 16px;font-size:11px;font-weight:700;letter-spacing:.06em;text-transform:uppercase;color:var(--color-text-muted)">
            Fase {i + 1} · {p.title} <span style="font-weight:500">({p.done}/{p.total})</span>
          </div>
          {data.steps.filter(s => s.phase === p.key).map(s => {
            const active = s.key === current.key;
            return (
              <a key={s.key} href={`/inrichten/${s.key}`} onClick={e => { e.preventDefault(); route(`/inrichten/${s.key}`); }}
                style={`display:flex;align-items:center;gap:10px;padding:7px 16px;font-size:14px;text-decoration:none;color:var(--color-text);${active ? 'background:var(--color-bg-secondary);font-weight:600;box-shadow:inset 3px 0 0 var(--color-primary)' : ''}`}>
                <span style={`width:20px;height:20px;flex:none;border-radius:50%;display:flex;align-items:center;justify-content:center;font-size:11px;${s.status === 'done' ? 'background:var(--color-success);color:#fff' : `border:1.5px solid var(--color-border);color:var(--color-text-muted)`}`}>
                  {STATUS_ICON[s.status]}
                </span>
                <span style="flex:1;min-width:0">{s.title}</span>
              </a>
            );
          })}
        </div>
      ))}
    </div>
  );
}

function StepPanel({ step, next, data }) {
  const [busy, setBusy] = useState(false);
  // Na het afvinken door naar de volgende stap, maar niet als je intussen al ergens anders bent
  const mounted = useRef(true);
  useEffect(() => () => { mounted.current = false; }, []);
  const phaseNo = data.phases.findIndex(p => p.key === step.phase) + 1;
  const lock = step.module ? lockOf(step.module) : null;

  const [error, setError] = useState('');
  const mark = async status => {
    setBusy(true); setError('');
    try {
      await changeSetup(`/api/setup/steps/${step.key}`, { method: 'PUT', body: JSON.stringify({ status }) });
      if (mounted.current && status && next) route(`/inrichten/${next.key}`);
    } catch {
      setError('Opslaan lukte niet. Probeer het nog eens.');
    } finally { setBusy(false); }
  };

  return (
    <div class="card" style="padding:24px">
      <div class="text-sm text-muted" style="margin-bottom:4px">Fase {phaseNo} · {data.phases[phaseNo - 1].title}</div>
      <div style="display:flex;align-items:center;gap:10px;flex-wrap:wrap">
        <h2 style="font-size:22px;font-weight:800;margin:0">{step.title}</h2>
        {step.status === 'done' && <span class="badge badge-afgerond">Klaar</span>}
        {step.status === 'later' && <span class="badge badge-open">Voor later</span>}
        {lock && <span class="badge" style="background:var(--color-bg-secondary)">🔒 Onderdeel van {lock.tierLabel}</span>}
      </div>
      <p style="font-size:16px;margin:10px 0 0;line-height:1.55">{step.why}</p>
      {step.solves?.length > 0 && (
        <ul style="margin:12px 0 0;padding:0;list-style:none;display:grid;gap:6px">
          {step.solves.map(s => (
            <li key={s} style="display:flex;gap:8px;line-height:1.5"><span style="color:var(--color-success);font-weight:700">✓</span><span>{s}</span></li>
          ))}
        </ul>
      )}

      {step.video && (
        // Why preload none en geen autoplay: het filmpje (met muziek) start pas als iemand op afspelen klikt
        <video key={step.video} src={`/assets/setup/${step.video}.mp4`} poster={`/assets/setup/${step.video}.jpg`}
          controls preload="none" playsInline aria-label={`Filmpje: ${step.title}`}
          style="display:block;width:100%;max-width:720px;aspect-ratio:16/9;margin-top:18px;border-radius:12px;background:#0d1b38;border:1px solid var(--color-border)" />
      )}

      {step.key === 'profiel' && <ProfileStep onDone={() => mark('done')} />}
      {step.key === 'seizoen' && <SeasonStep />}
      {step.key === 'structuur' && <StructureStep suggestions={data.suggestions} />}
      {step.key === 'rollen' && <RolesStep />}
      {step.key === 'jaarplanning' && <JaarplanningStep />}
      {step.key === 'processen' && <RecurringStep data={data} />}
      {step.key === 'vergaderingen' && <BodiesStep data={data} />}
      {step.key === 'documenten' && <PolicyLinksStep data={data} />}

      {step.related?.length > 0 && (
        <div style="margin-top:22px">
          <div style="font-size:13px;font-weight:700;margin-bottom:8px">Hangt samen met</div>
          <div style="display:flex;flex-wrap:wrap;gap:8px">
            {step.related.map(([label, href]) => (
              <a key={href + label} href={href} onClick={e => { e.preventDefault(); route(href); }}
                style="font-size:13px;padding:5px 12px;border-radius:999px;background:var(--color-bg-secondary);color:var(--color-text);text-decoration:none;border:1px solid var(--color-border)">
                {label} →
              </a>
            ))}
          </div>
        </div>
      )}

      <div style="display:flex;flex-wrap:wrap;gap:10px;margin-top:26px;padding-top:18px;border-top:1px solid var(--color-border)">
        {!step.detail && <Button variant="secondary" onClick={() => route(step.href)}>Naar {step.title.toLowerCase()} →</Button>}
        {/* Ook bij een stap die zichzelf afvinkt: een club die Kwalificaties niet gebruikt,
            moet hem toch kunnen afvinken, anders komt de route nooit op 16 van 16 */}
        {step.status !== 'done' && <Button disabled={busy} onClick={() => mark('done')}>Klaar</Button>}
        {step.status === 'open' && <Button variant="ghost" disabled={busy} onClick={() => mark('later')}>Later doen</Button>}
        {step.status !== 'open' && !step.autoDone && <Button variant="ghost" disabled={busy} onClick={() => mark(null)}>Weer openzetten</Button>}
        {step.auto && step.status !== 'done' && <span class="text-sm text-muted" style="align-self:center">Deze stap vinkt zichzelf af zodra hij gedaan is.</span>}
        {next && <Button variant="ghost" style="margin-left:auto" onClick={() => route(`/inrichten/${next.key}`)}>Volgende: {next.title} →</Button>}
      </div>
      {error && <div class="alert alert-error" style="margin-top:10px">{error}</div>}
    </div>
  );
}

// ── Fase 1: clubprofiel ─────────────────────────────────────────────────────────
function ProfileStep({ onDone }) {
  const theme = useTheme();
  // Why null tot iemand de kleur aanpast: het thema kan nog laden als dit scherm opent, en
  // dan zou Opslaan de clubkleur terugzetten naar de standaardkleur
  const [color, setColor] = useState(null);
  const [logo, setLogo] = useState(null);
  const [state, setState] = useState('');

  const save = async () => {
    if (!color && !logo) { setState('nothing'); return; }
    setState('saving');
    const form = new FormData();
    if (color) form.append('primary_color', color);
    if (logo) form.append('logo', logo);
    try {
      const res = await fetch('/api/clubs/settings', { method: 'PUT', body: form });
      if (!res.ok) { setState('error'); return; }
      await loadTheme();
      setState('saved');
    } catch { setState('error'); }
  };

  return (
    <div class="card" style="margin-top:20px;padding:18px;background:var(--color-bg-secondary)">
      <div style="display:flex;gap:18px;align-items:center;flex-wrap:wrap">
        <div style="width:64px;height:64px;border-radius:14px;background:var(--color-surface);display:flex;align-items:center;justify-content:center;border:1px solid var(--color-border)">
          {theme.logo ? <img src={theme.logo} alt="" style="max-width:52px;max-height:52px" /> : <span style="font-size:26px;font-weight:800">{(theme.name || '?')[0]}</span>}
        </div>
        <div style="flex:1;min-width:220px">
          <div style="font-weight:700">{theme.name}</div>
          <div class="text-sm text-muted">Tot jullie een eigen logo kiezen, staat hier het logo van jullie sport.</div>
        </div>
      </div>
      <div style="display:flex;gap:16px;flex-wrap:wrap;margin-top:16px;align-items:flex-end">
        <label class="form-label" style="margin:0">Eigen logo
          <input class="form-input" type="file" accept="image/*" onChange={e => setLogo(e.target.files?.[0] || null)} />
        </label>
        <label class="form-label" style="margin:0">Clubkleur
          <input type="color" value={color || theme.colors?.primary || '#16a34a'} onInput={e => setColor(e.target.value)} style="display:block;width:64px;height:38px;border:1px solid var(--color-border);border-radius:8px;background:none;padding:2px" />
        </label>
        <Button disabled={state === 'saving'} onClick={save}>{state === 'saving' ? 'Opslaan…' : 'Opslaan'}</Button>
        {state === 'saved' && <Button variant="secondary" onClick={onDone}>Ziet er goed uit, klaar</Button>}
      </div>
      {state === 'error' && <div class="alert alert-error" style="margin-top:10px">Opslaan lukte niet. Is het logo een afbeelding van minder dan 10 MB?</div>}
      {state === 'nothing' && <div class="text-sm text-muted" style="margin-top:10px">Kies eerst een logo of een andere kleur.</div>}
      <div class="text-sm text-muted" style="margin-top:10px">Meer kleuren en een banner stel je in bij Instellingen.</div>
    </div>
  );
}

// ── Fase 1: seizoen ─────────────────────────────────────────────────────────────
function seasonDefaults() {
  const now = new Date();
  const y = now.getMonth() >= 6 ? now.getFullYear() : now.getFullYear() - 1; // seizoen loopt van juli tot juli
  return { name: `${y}-${y + 1}`, start_date: `${y}-07-01`, end_date: `${y + 1}-06-30` };
}

function SeasonStep() {
  const [active, setActive] = useState(undefined);
  const [form, setForm] = useState(seasonDefaults);
  const [error, setError] = useState('');
  useEffect(() => { api('/api/seasons/active').then(s => setActive(s || null)).catch(() => setActive(null)); }, []);

  const create = async () => {
    setError('');
    if (!form.name.trim()) { setError('Geef het seizoen een naam.'); return; }
    if (form.start_date && form.end_date && form.end_date <= form.start_date) { setError('Het einde moet na het begin liggen.'); return; }
    try {
      const s = await api('/api/seasons', { method: 'POST', body: JSON.stringify({ ...form, make_active: true }) });
      setActive(s);
      await loadSetup({ force: true });
    } catch { setError('Aanmaken lukte niet. Probeer het nog eens.'); }
  };

  if (active === undefined) return null;
  if (active) {
    return (
      <div class="card" style="margin-top:20px;padding:18px;background:var(--color-bg-secondary)">
        <strong>Seizoen {active.name}</strong> is actief{active.start_date ? `, van ${fmt(active.start_date)} tot en met ${fmt(active.end_date)}` : ''}.
        <div class="text-sm text-muted" style="margin-top:6px">Aan het eind van het seizoen start je bij Seizoenbeheer het volgende; het oude blijft terug te vinden.</div>
      </div>
    );
  }
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  return (
    <div class="card" style="margin-top:20px;padding:18px;background:var(--color-bg-secondary)">
      <div class="text-sm text-muted" style="margin-bottom:12px">De meeste clubs laten het seizoen op 1 juli beginnen. Pas het aan als het bij jullie anders loopt.</div>
      <div style="display:flex;gap:12px;flex-wrap:wrap;align-items:flex-end">
        <label class="form-label" style="margin:0">Naam<input class="form-input" value={form.name} onInput={e => set('name', e.target.value)} style="width:140px" /></label>
        <label class="form-label" style="margin:0">Begint op<input class="form-input" type="date" value={form.start_date} onInput={e => set('start_date', e.target.value)} /></label>
        <label class="form-label" style="margin:0">Eindigt op<input class="form-input" type="date" value={form.end_date} onInput={e => set('end_date', e.target.value)} /></label>
        <Button onClick={create}>Seizoen aanmaken</Button>
      </div>
      {error && <div class="alert alert-error" style="margin-top:10px">{error}</div>}
    </div>
  );
}

const fmt = d => new Date(`${String(d).slice(0, 10)}T12:00:00`).toLocaleDateString('nl-NL', { day: 'numeric', month: 'long', year: 'numeric' });

// ── Fase 1: structuur ───────────────────────────────────────────────────────────
// De voorbeelden als boom met vinkjes; namen zijn aan te passen. Wat er al staat, krijgt
// het label "staat er al" en wordt bij overnemen niet dubbel aangemaakt.
function toTree(structure) {
  return structure.map(s => ({
    name: s.name, about: s.about, on: true,
    functions: (s.functions || []).map(f => ({ name: f, on: true })),
    commissions: (s.commissions || []).map(c => ({ name: c.name, on: true, functions: (c.functions || []).map(f => ({ name: f, on: true })) })),
  }));
}

// Eén regel in de structuurboom. Buiten StructureStep: binnenin zou elke toetsaanslag een
// nieuw onderdeel geven en verlies je de focus in het naamveld.
function TreeItem({ item, kind, exists, level, onToggle, onRename }) {
  return (
    <div style={`display:flex;align-items:center;gap:8px;padding:3px 0 3px ${level * 22}px`}>
      <input type="checkbox" checked={item.on} onChange={onToggle} aria-label={`${item.name} overnemen`} />
      <input class="form-input" value={item.name} onInput={e => onRename(e.target.value)} disabled={!item.on} aria-label="Naam"
        style={`height:30px;padding:0 8px;font-size:13px;max-width:260px;${level === 0 ? 'font-weight:700' : ''}`} />
      <span class="text-sm text-muted" style="min-width:64px">{kind}</span>
      {exists && <span class="text-sm text-muted">· staat er al</span>}
    </div>
  );
}

function StructureStep({ suggestions }) {
  const [tree, setTree] = useState(() => toTree(suggestions?.structure || []));
  const [existing, setExisting] = useState(null);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const loadExisting = () => api('/api/settings/sections').then(list => {
    const names = new Set();
    for (const s of list || []) {
      names.add(`s:${s.name}`);
      for (const c of s.commissions || []) { names.add(`c:${s.name}:${c.name}`); for (const f of c.categories || []) names.add(`f:${s.name}:${f.name}`); }
      for (const f of s.categories || []) names.add(`f:${s.name}:${f.name}`);
    }
    setExisting(names);
  }).catch(() => setExisting(new Set()));
  useEffect(() => { loadExisting(); }, []);

  const update = fn => setTree(t => { const copy = JSON.parse(JSON.stringify(t)); fn(copy); return copy; });

  const apply = async () => {
    const sections = tree.filter(s => s.on && s.name.trim()).map(s => ({
      name: s.name,
      functions: s.functions.filter(f => f.on).map(f => f.name),
      commissions: s.commissions.filter(c => c.on && c.name.trim()).map(c => ({ name: c.name, functions: c.functions.filter(f => f.on).map(f => f.name) })),
    }));
    if (!sections.length) { setError('Vink minstens één sectie aan.'); return; }
    setError(''); setBusy(true);
    try {
      const res = await changeSetup('/api/setup/structure', { method: 'POST', body: JSON.stringify({ sections }) });
      setResult(res.created);
      loadExisting();
    } catch { setError('Overnemen lukte niet. Probeer het nog eens.'); }
    finally { setBusy(false); }
  };

  const sportLabel = suggestions?.sport === 'algemeen' ? 'een sportclub' : `een ${suggestions?.sport}club`;
  return (
    <div style="margin-top:20px">
      <div class="card" style="padding:14px 18px;background:var(--color-bg-secondary);margin-bottom:14px">
        <strong>Zo werkt het.</strong> Een <em>sectie</em> is een groot deel van de club, zoals de jeugd. Een <em>commissie</em> is een groep die samen iets regelt.
        Een <em>functie</em> is wat iemand doet, zoals trainer of jeugdcoördinator. Leden koppel je straks aan hun functies.
      </div>
      <div style="font-size:14px;font-weight:700;margin-bottom:6px">Voorbeeld voor {sportLabel}</div>
      <div class="text-sm text-muted" style="margin-bottom:10px">Vink uit wat jullie niet hebben en pas namen aan. Later aanvullen kan altijd bij Secties & functies.</div>
      {tree.map((s, si) => (
        <div key={si} class="card" style="padding:12px 16px;margin-bottom:10px">
          <TreeItem item={s} kind="sectie" exists={existing?.has(`s:${s.name}`)} level={0} onToggle={() => update(t => { t[si].on = !t[si].on; })} onRename={v => update(t => { t[si].name = v; })} />
          {s.about && <div class="text-sm text-muted" style="padding-left:22px;margin-bottom:4px">{s.about}</div>}
          {s.on && s.functions.map((f, fi) => (
            <TreeItem key={`f${fi}`} item={f} kind="functie" exists={existing?.has(`f:${s.name}:${f.name}`)} level={1} onToggle={() => update(t => { const x = t[si].functions[fi]; x.on = !x.on; })} onRename={v => update(t => { t[si].functions[fi].name = v; })} />
          ))}
          {s.on && s.commissions.map((c, ci) => (
            <div key={`c${ci}`}>
              <TreeItem item={c} kind="commissie" exists={existing?.has(`c:${s.name}:${c.name}`)} level={1} onToggle={() => update(t => { t[si].commissions[ci].on = !t[si].commissions[ci].on; })} onRename={v => update(t => { t[si].commissions[ci].name = v; })} />
              {c.on && c.functions.map((f, fi) => (
                <TreeItem key={fi} item={f} kind="functie" exists={existing?.has(`f:${s.name}:${f.name}`)} level={2} onToggle={() => update(t => { const x = t[si].commissions[ci].functions[fi]; x.on = !x.on; })} onRename={v => update(t => { t[si].commissions[ci].functions[fi].name = v; })} />
              ))}
            </div>
          ))}
        </div>
      ))}
      <div style="display:flex;gap:12px;align-items:center;flex-wrap:wrap;margin-top:6px">
        <Button disabled={busy} onClick={apply}>{busy ? 'Bezig…' : 'Aangevinkte structuur overnemen'}</Button>
        {result && <span class="text-sm">Toegevoegd: {result.sections} secties, {result.commissions} commissies en {result.functions} functies. Elke commissie en functie krijgt een eigen documentenmap; een sectie krijgt de zijne zodra er een commissie of functie in zit.</span>}
      </div>
      {error && <div class="alert alert-error" style="margin-top:10px">{error}</div>}
    </div>
  );
}

// ── Fase 1: rollen ──────────────────────────────────────────────────────────────
// De standaardrollen in gewone taal: wie krijgt hem. Een rol die de club zelf maakte,
// toont zijn eigen omschrijving.
const ROLE_HINTS = {
  'Functioneel Beheerder': 'Wie de app inricht en beheert. Meestal één of twee mensen.',
  'Bestuur': 'De bestuursleden: processen, taken en documenten beheren en rapportages inzien.',
  'Commissielid': 'Leden van een commissie: processen inzien, hun taken bijwerken en documenten toevoegen.',
  'Secretaris': 'Voor de secretaris: processen, taken en documenten beheren.',
  'Penningmeester': 'Voor de penningmeester: processen, taken en documenten beheren.',
  'Nieuwsredacteur': 'Wie nieuwsberichten schrijft voor de leden.',
  'Sjablonenbeheerder': 'Wie de vaste werkwijzen (sjablonen) bijhoudt.',
  'Documentbeheerder': 'Wie documenten en de beleidsbibliotheek beheert.',
  'Seizoensbeheerder': 'Wie seizoenen en de jaarplanning bijhoudt.',
  'Vrijwilliger': 'Trainers, leiders en andere vrijwilligers: hun eigen taken en documenten.',
  'Lees-only': 'Adviseurs of sponsoren die alles mogen inzien maar niets wijzigen.',
};

function RolesStep() {
  const [roles, setRoles] = useState(null);
  useEffect(() => { api('/api/roles').then(setRoles).catch(() => setRoles([])); }, []);
  if (!roles) return null;
  return (
    <div style="margin-top:20px">
      <div class="text-sm text-muted" style="margin-bottom:10px">Iemand kan meerdere rollen hebben. Rollen toewijzen doe je bij het uitnodigen van het kernteam (fase 2).</div>
      <div class="card" style="padding:0">
        {roles.map((r, i) => (
          <div key={r.id} style={`display:flex;gap:12px;padding:10px 16px;${i ? 'border-top:1px solid var(--color-border)' : ''}`}>
            <div style="width:180px;flex:none;font-weight:600">{r.name}</div>
            <div class="text-sm" style="flex:1">{ROLE_HINTS[r.name] || r.description}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Fase 3: jaarplanning ────────────────────────────────────────────────────────
// De startersset uit platformbeheer, per maand uitklapbaar. Je vinkt aan wat bij jullie
// past; de rollen in de set koppelen bij het overnemen op naam aan jullie commissies.
const MAANDEN = ['januari', 'februari', 'maart', 'april', 'mei', 'juni',
  'juli', 'augustus', 'september', 'oktober', 'november', 'december'];
// Het seizoen begint in juli: zo staat de lijst in de volgorde waarin het jaar verloopt
const SEIZOENSVOLGORDE = ['07', '08', '09', '10', '11', '12', '01', '02', '03', '04', '05', '06'];

function JaarplanningStep() {
  const [set, setSet] = useState(undefined);
  const [seizoen, setSeizoen] = useState(undefined);
  const [uit, setUit] = useState(() => new Set());
  const [open, setOpen] = useState(() => new Set());
  const [busy, setBusy] = useState(false);
  const [resultaat, setResultaat] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    api('/api/jaarplanning/starter-sets').then(s => setSet(s?.[0] || null)).catch(() => setSet(null));
    api('/api/seasons/active').then(s => setSeizoen(s || null)).catch(() => setSeizoen(null));
  }, []);

  if (set === undefined || seizoen === undefined) return null;
  if (!set) return null;
  if (!seizoen) {
    return (
      <div class="card" style="margin-top:20px;padding:18px;background:var(--color-bg-secondary)">
        <strong>Eerst een seizoen</strong>
        <div class="text-sm text-muted" style="margin-top:6px">
          De jaarplanning loopt per maand door het seizoen, dus die moet er eerst zijn.
        </div>
        <Button variant="secondary" style="margin-top:12px" onClick={() => route('/inrichten/seizoen')}>Naar de stap Seizoen →</Button>
      </div>
    );
  }

  const perMaand = SEIZOENSVOLGORDE
    .map(m => ({ maand: m, items: (set.activities || []).filter(a => a.planning_month === m) }))
    .filter(g => g.items.length);
  const aan = (set.activities || []).filter(a => !uit.has(a.id));
  const wissel = (verzameling, id, zet) => {
    const next = new Set(verzameling);
    zet(next.has(id) ? (next.delete(id), next) : (next.add(id), next));
  };

  const overnemen = async () => {
    setError(''); setBusy(true);
    try {
      const r = await api(`/api/jaarplanning/starter-sets/${set.id}/import`, {
        method: 'POST',
        body: JSON.stringify({ season_id: seizoen.id, activity_ids: aan.map(a => a.id) }),
      });
      setResultaat(r);
      await loadSetup({ force: true });
    } catch { setError('Overnemen lukte niet. Probeer het nog eens.'); }
    setBusy(false);
  };

  return (
    <div class="card" style="margin-top:20px;padding:18px;background:var(--color-bg-secondary)">
      <div class="text-sm text-muted" style="margin-bottom:12px">{set.description}</div>
      {perMaand.map(({ maand, items }) => {
        const uitgeklapt = open.has(maand);
        const telling = items.filter(a => !uit.has(a.id)).length;
        return (
          <div key={maand} style="border-top:1px solid var(--color-border)">
            <button type="button" onClick={() => wissel(open, maand, setOpen)}
              style="display:flex;width:100%;gap:10px;align-items:center;background:none;border:none;padding:9px 2px;cursor:pointer;text-align:left;font:inherit;color:inherit">
              <span style="width:14px;color:var(--color-text-muted)">{uitgeklapt ? '▾' : '▸'}</span>
              <strong style="flex:1;text-transform:capitalize">{MAANDEN[Number(maand) - 1]}</strong>
              <span class="text-sm text-muted">{telling} van {items.length}</span>
            </button>
            {uitgeklapt && (
              <div style="padding:0 2px 10px 26px;display:flex;flex-direction:column;gap:5px">
                {items.map(a => (
                  <label key={a.id} style="display:flex;gap:8px;align-items:flex-start;font-size:13px;cursor:pointer">
                    <input type="checkbox" checked={!uit.has(a.id)} onChange={() => wissel(uit, a.id, setUit)} />
                    <span>{a.name}</span>
                  </label>
                ))}
              </div>
            )}
          </div>
        );
      })}
      <div style="display:flex;gap:12px;align-items:center;flex-wrap:wrap;margin-top:14px;padding-top:14px;border-top:1px solid var(--color-border)">
        <Button disabled={busy || !aan.length} onClick={overnemen}>
          {busy ? 'Bezig…' : `${aan.length} activiteiten overnemen`}
        </Button>
        {resultaat && (
          <span class="text-sm">
            Overgenomen in {resultaat.season}: {resultaat.imported} activiteiten, {resultaat.assign_imported} keer een commissie eraan gekoppeld.
            {resultaat.skipped ? ` ${resultaat.skipped} stonden er al.` : ''}
          </span>
        )}
      </div>
      <div class="text-sm text-muted" style="margin-top:8px">
        Een commissie uit de set die jullie niet hebben, wordt overgeslagen. Aanvullen en aanpassen doe je daarna bij Jaarplanning.
      </div>
      {error && <div class="alert alert-error" style="margin-top:10px">{error}</div>}
    </div>
  );
}

// ── Fase 3: terugkerend werk ────────────────────────────────────────────────────
// Voorstellen op de sjablonen die de club al heeft. Zonder Plus laten we wel zien wat
// het oplevert, maar is er niets aan te zetten.
function RecurringStep({ data }) {
  // Na het aanmaken geeft de server de bijgewerkte lijst terug; die wint van wat er stond
  const [verwerkt, setVerwerkt] = useState(null);
  const voorstellen = verwerkt || data?.recurring?.suggestions || [];
  const heeftModule = !!data?.recurring?.module;
  const [aan, setAan] = useState(() => new Set(voorstellen.filter(s => !s.exists).map(s => s.key)));
  const [busy, setBusy] = useState(false);
  const [resultaat, setResultaat] = useState(null);
  const [error, setError] = useState('');

  if (!voorstellen.length) return null;
  const gekozen = voorstellen.filter(s => !s.exists && aan.has(s.key));

  const wissel = key => setAan(prev => {
    const next = new Set(prev);
    next.has(key) ? next.delete(key) : next.add(key);
    return next;
  });

  const aanmaken = async () => {
    setError(''); setBusy(true);
    try {
      const r = await changeSetup('/api/setup/recurring', { method: 'POST', body: JSON.stringify({ keys: gekozen.map(s => s.key) }) });
      setResultaat(r);
      if (r.recurring?.suggestions) setVerwerkt(r.recurring.suggestions);
    } catch { setError('Aanmaken lukte niet. Probeer het nog eens.'); }
    setBusy(false);
  };

  return (
    <div class="card" style="margin-top:20px;padding:18px;background:var(--color-bg-secondary)">
      <strong>Laat deze processen zichzelf starten</strong>
      <div class="text-sm text-muted" style="margin-top:4px;margin-bottom:12px">
        Op de sjablonen die jullie al hebben. De app start het proces op de dag die erbij staat, en maakt meteen de taken aan.
      </div>
      <div style="display:flex;flex-direction:column;gap:9px">
        {voorstellen.map(s => (
          <label key={s.key} style={`display:flex;gap:9px;align-items:flex-start;cursor:${s.exists || !heeftModule ? 'default' : 'pointer'};opacity:${s.exists ? 0.6 : 1}`}>
            <input type="checkbox" style="margin-top:3px" disabled={s.exists || !heeftModule}
              checked={s.exists || aan.has(s.key)} onChange={() => wissel(s.key)} />
            <span>
              <strong style="font-size:13px">{s.template}</strong>
              {s.exists
                ? <span class="text-sm text-muted"> — staat er al</span>
                : <span class="text-sm text-muted"> — voor het eerst op {fmt(s.next_run_at)}</span>}
              <div class="text-sm text-muted">{s.why}</div>
            </span>
          </label>
        ))}
      </div>
      {heeftModule ? (
        <div style="display:flex;gap:12px;align-items:center;flex-wrap:wrap;margin-top:14px">
          <Button disabled={busy || !gekozen.length} onClick={aanmaken}>
            {busy ? 'Bezig…' : `${gekozen.length} ${gekozen.length === 1 ? 'planning' : 'planningen'} aanmaken`}
          </Button>
          {resultaat && <span class="text-sm">Aangemaakt: {resultaat.created}.{resultaat.skipped ? ` ${resultaat.skipped} stonden er al.` : ''} Je vindt ze terug bij Terugkerende processen.</span>}
        </div>
      ) : (
        <div class="text-sm text-muted" style="margin-top:14px">
          🔒 Een proces vanzelf laten starten zit in Plus. Op Basis start je het zelf bij Processen, met dezelfde sjablonen.
        </div>
      )}
      {error && <div class="alert alert-error" style="margin-top:10px">{error}</div>}
    </div>
  );
}

// ── Fase 3: vergadergremia ──────────────────────────────────────────────────────
// Een gremium per commissie die de club heeft. Wie daar een functie in heeft, is vanzelf lid.
function BodiesStep({ data }) {
  const [verwerkt, setVerwerkt] = useState(null);
  const voorstellen = verwerkt || data?.bodies?.suggestions || [];
  const heeftModule = !!data?.bodies?.module;
  const [uit, setUit] = useState(() => new Set());
  const [busy, setBusy] = useState(false);
  const [resultaat, setResultaat] = useState(null);
  const [error, setError] = useState('');

  if (!voorstellen.length) {
    return (
      <div class="card" style="margin-top:20px;padding:18px;background:var(--color-bg-secondary)">
        <strong>Eerst commissies</strong>
        <div class="text-sm text-muted" style="margin-top:6px">
          Een gremium hangt aan een commissie; wie daar een functie in heeft, is dan vanzelf lid.
        </div>
        <Button variant="secondary" style="margin-top:12px" onClick={() => route('/inrichten/structuur')}>Naar de stap Structuur →</Button>
      </div>
    );
  }
  const aan = voorstellen.filter(s => !s.exists && !uit.has(s.key));

  const overnemen = async () => {
    setError(''); setBusy(true);
    try {
      const r = await changeSetup('/api/setup/bodies', { method: 'POST', body: JSON.stringify({ keys: aan.map(s => s.key) }) });
      setResultaat(r);
      if (r.bodies?.suggestions) setVerwerkt(r.bodies.suggestions);
    } catch { setError('Aanmaken lukte niet. Probeer het nog eens.'); }
    setBusy(false);
  };

  return (
    <div class="card" style="margin-top:20px;padding:18px;background:var(--color-bg-secondary)">
      <strong>Maak een vergadergremium per commissie</strong>
      <div class="text-sm text-muted" style="margin-top:4px;margin-bottom:12px">
        Iedereen met een functie in die commissie is er vanzelf lid van. Daarna plan je er vergaderingen in, met agenda, notulen en besluiten.
      </div>
      <div style="display:flex;flex-direction:column;gap:7px">
        {voorstellen.map(s => (
          <label key={s.key} style={`display:flex;gap:9px;align-items:baseline;cursor:${s.exists || !heeftModule ? 'default' : 'pointer'};opacity:${s.exists ? 0.6 : 1}`}>
            <input type="checkbox" disabled={s.exists || !heeftModule} checked={s.exists || !uit.has(s.key)}
              onChange={() => setUit(prev => { const n = new Set(prev); n.has(s.key) ? n.delete(s.key) : n.add(s.key); return n; })} />
            <span style="font-size:13px">
              <strong>{s.name}</strong>
              <span class="text-sm text-muted"> — {s.section}
                {s.exists ? ' · vergadert al' : s.members ? ` · ${s.members} ${s.members === 1 ? 'lid' : 'leden'}` : ' · nog geen leden'}</span>
            </span>
          </label>
        ))}
      </div>
      {heeftModule ? (
        <div style="display:flex;gap:12px;align-items:center;flex-wrap:wrap;margin-top:14px">
          <Button disabled={busy || !aan.length} onClick={overnemen}>
            {busy ? 'Bezig…' : `${aan.length} ${aan.length === 1 ? 'gremium' : 'gremia'} aanmaken`}
          </Button>
          {resultaat && <span class="text-sm">Aangemaakt: {resultaat.created}.{resultaat.skipped ? ` ${resultaat.skipped} bestonden al.` : ''}</span>}
        </div>
      ) : (
        <div class="text-sm text-muted" style="margin-top:14px">
          🔒 Vergaderen met agenda, notulen en besluiten zit in Plus.
        </div>
      )}
      {error && <div class="alert alert-error" style="margin-top:10px">{error}</div>}
    </div>
  );
}

// ── Fase 4: beleidsbibliotheek ──────────────────────────────────────────────────
// Statuten en reglementen zijn van de club zelf; die kan de route niet klaarzetten.
// Wat wel kan: verwijzingen naar de bronnen waar elke vereniging mee te maken heeft.
function PolicyLinksStep({ data }) {
  const [verwerkt, setVerwerkt] = useState(null);
  const links = verwerkt || data?.policyLinks?.suggestions || [];
  const magToevoegen = !!data?.policyLinks?.mayAdd;
  const [uit, setUit] = useState(() => new Set());
  const [busy, setBusy] = useState(false);
  const [resultaat, setResultaat] = useState(null);
  const [error, setError] = useState('');
  if (!links.length) return null;
  const aan = links.filter(l => !l.exists && !uit.has(l.key));

  const overnemen = async () => {
    setError(''); setBusy(true);
    try {
      const r = await changeSetup('/api/setup/policy-links', { method: 'POST', body: JSON.stringify({ keys: aan.map(l => l.key) }) });
      setResultaat(r);
      if (r.policyLinks?.suggestions) setVerwerkt(r.policyLinks.suggestions);
    } catch { setError('Toevoegen lukte niet. Probeer het nog eens.'); }
    setBusy(false);
  };

  return (
    <div class="card" style="margin-top:20px;padding:18px;background:var(--color-bg-secondary)">
      <strong>Zet de bronnen alvast klaar</strong>
      <div class="text-sm text-muted" style="margin-top:4px;margin-bottom:12px">
        Jullie eigen statuten en reglementen upload je zelf. Deze verwijzingen helpen bij het schrijven ervan en komen in de beleidsbibliotheek, waar elk lid ze kan lezen.
      </div>
      <div style="display:flex;flex-direction:column;gap:8px">
        {links.map(l => (
          <label key={l.key} style={`display:flex;gap:9px;align-items:flex-start;cursor:${l.exists ? 'default' : 'pointer'};opacity:${l.exists ? 0.6 : 1}`}>
            <input type="checkbox" style="margin-top:3px" disabled={l.exists || !magToevoegen} checked={l.exists || !uit.has(l.key)}
              onChange={() => setUit(prev => { const n = new Set(prev); n.has(l.key) ? n.delete(l.key) : n.add(l.key); return n; })} />
            <span>
              <strong style="font-size:13px">{l.name}</strong>
              <span class="text-sm text-muted"> — {l.section}{l.exists ? ' · staat er al' : ''}</span>
              <div class="text-sm text-muted">{l.description}</div>
            </span>
          </label>
        ))}
      </div>
      {magToevoegen ? (
        <div style="display:flex;gap:12px;align-items:center;flex-wrap:wrap;margin-top:14px">
          <Button disabled={busy || !aan.length} onClick={overnemen}>
            {busy ? 'Bezig…' : `${aan.length} ${aan.length === 1 ? 'verwijzing' : 'verwijzingen'} toevoegen`}
          </Button>
          {resultaat && <span class="text-sm">Toegevoegd: {resultaat.created}.{resultaat.skipped ? ` ${resultaat.skipped} stonden er al.` : ''}</span>}
        </div>
      ) : (
        <div class="text-sm text-muted" style="margin-top:14px">
          Toevoegen doet iemand met het recht Documenten; vraag het aan je documentbeheerder.
        </div>
      )}
      {error && <div class="alert alert-error" style="margin-top:10px">{error}</div>}
    </div>
  );
}
